import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ShieldAlert,
  Zap,
  UserCheck,
  GitPullRequest,
  ClipboardCheck,
  Users,
  Wallet,
  TrendingUp,
  Gauge,
  KeyRound,
  Receipt,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { useAuth } from '@/features/auth';
import { useCase360Governance } from '../hooks/useCase360Governance';
import { useCase360PendingApprovals } from '../hooks/useCase360PendingApprovals';
import type { Case360Governance, Case360PendingCounts } from '../api/dashboard.api';
import {
  BreakGlassRequestsDialog,
  DelegationsDialog,
  ChangeRequestsDialog,
} from './PendingApprovalDialogs';

// Stage 13 — at most this many per-matter budget rows in the alerts card.
const BUDGET_ALERT_LIMIT = 5;

interface RateAlert {
  key: string;
  severity: 'critical' | 'warning';
  icon: React.ReactNode;
  title: string;
  description: string;
  value: string;
  threshold: string;
}

function AlertRow({ alert }: { alert: RateAlert }) {
  const critical = alert.severity === 'critical';
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 ${
        critical ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className={critical ? 'text-red-600' : 'text-amber-600'}>{alert.icon}</span>
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${critical ? 'text-red-800' : 'text-amber-800'}`}>
            {alert.title}
          </p>
          <p className="text-xs text-slate-600 truncate">{alert.description}</p>
        </div>
      </div>
      <div className="shrink-0 text-right">
        <span className={`text-sm font-bold ${critical ? 'text-red-700' : 'text-amber-700'}`}>
          {alert.value}
        </span>
        <span className="text-xs text-slate-500"> {alert.threshold}</span>
      </div>
    </div>
  );
}

/**
 * Case 360° (Stage 12) — Partner/MD governance view: real BYPASS/Emergency
 * rate alerts (single red-priority slot at a time, same-type items grouped),
 * pending approvals (Compliance, Emergency, Delegations, Change Requests)
 * and team load. Read-only over data owned by Stages 2/5/6/11.
 *
 * Each card is exported as its own dashboard block so they drag-reorder
 * independently; they share one `useCase360Governance` query (React Query
 * dedupes the fetch).
 */
function useGovernance() {
  const { hasAccess } = useAuth();
  const enabled = hasAccess('governance-statistic', 'get');
  const { data, isLoading } = useCase360Governance(enabled);
  return { enabled, data, isLoading };
}

export function GovernanceAlertsBlock() {
  const { enabled, data, isLoading } = useGovernance();
  if (!enabled) return null;
  return <AlertsCard data={data} isLoading={isLoading} />;
}

export function GovernanceTeamLoadBlock() {
  const { enabled, data, isLoading } = useGovernance();
  if (!enabled) return null;
  return <TeamLoadCard data={data} isLoading={isLoading} />;
}

export function GovernanceApprovalsBlock() {
  const { hasAccess } = useAuth();
  const enabled = hasAccess('governance-statistic', 'get');
  const { data, isLoading } = useCase360PendingApprovals(enabled);
  if (!enabled) return null;
  return <PendingApprovalCountsCard counts={data} isLoading={isLoading} />;
}

// ── Alerts (always-on 4-tile metric strip + budget watchlist) ────────────────

type TileStatus = 'ok' | 'warning' | 'critical';

// The governance payload encodes only the critical trip (red/ok) for
// Emergency / ESC / Realization; BYPASS already ships 3 states. These add the
// amber "approaching" band the client mockup shows so every tile can render
// green → amber → red from one number.
const EMERGENCY_WARN = 0.03;
const EMERGENCY_CRIT = 0.05;
const ESC_WARN = 0.03;
const ESC_CRIT = 0.05;
const REALIZATION_WARN = 0.85;
const REALIZATION_CRIT = 0.8;

const TILE_TONES: Record<TileStatus, { box: string; value: string; icon: string }> = {
  ok: { box: 'border-emerald-200 bg-emerald-50/60', value: 'text-emerald-700', icon: 'text-emerald-500' },
  warning: { box: 'border-amber-200 bg-amber-50', value: 'text-amber-700', icon: 'text-amber-500' },
  critical: { box: 'border-red-200 bg-red-50', value: 'text-red-700', icon: 'text-red-600' },
};

/** Higher value = worse (rates). */
function worseAbove(value: number, warn: number, crit: number): TileStatus {
  if (value > crit) return 'critical';
  if (value >= warn) return 'warning';
  return 'ok';
}

/** Lower value = worse (realization). */
function worseBelow(value: number, warn: number, crit: number): TileStatus {
  if (value < crit) return 'critical';
  if (value < warn) return 'warning';
  return 'ok';
}

function MetricTile({
  icon,
  label,
  value,
  caption,
  status,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  caption: string;
  status: TileStatus;
  /** When set, the tile becomes a button that drills into the list behind the metric. */
  onClick?: () => void;
}) {
  const tone = TILE_TONES[status];
  const inner = (
    <>
      <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
        <span className={tone.icon}>{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <p className={`mt-1.5 text-2xl font-bold leading-none ${tone.value}`}>{value}</p>
      <p className="mt-1 text-[11px] text-slate-500 truncate">{caption}</p>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`block w-full rounded-lg border px-3 py-2.5 text-left transition-shadow cursor-pointer hover:shadow-md hover:ring-1 hover:ring-[#0069D1]/30 ${tone.box}`}
      >
        {inner}
      </button>
    );
  }

  return <div className={`rounded-lg border px-3 py-2.5 ${tone.box}`}>{inner}</div>;
}

function AlertsCard({ data, isLoading }: { data?: Case360Governance; isLoading: boolean }) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const bypass = data?.alerts?.bypass;
  const emergency = data?.alerts?.emergency;
  const escalation = data?.alerts?.escalation;
  const realization = data?.alerts?.realization;
  const budgetMatters = data?.alerts?.budget?.matters ?? [];

  const emergencyRate = emergency?.rate ?? 0;
  const bypassRate = bypass?.rate ?? 0;
  const escRate = escalation?.rate ?? 0;
  // No worked time ⇒ realization defaults to 1.0 (never trips the alert).
  const realizationValue = realization?.value ?? 1;

  const bypassStatus: TileStatus =
    bypass?.level === 'red' ? 'critical' : bypass?.level === 'yellow' ? 'warning' : 'ok';

  const tiles: {
    key: string;
    icon: React.ReactNode;
    label: string;
    value: string;
    caption: string;
    status: TileStatus;
    onClick?: () => void;
  }[] = [
    {
      key: 'emergency',
      icon: <Zap size={14} />,
      label: t('dashboard.case360.tileEmergency'),
      value: `${(emergencyRate * 100).toFixed(1)}%`,
      caption: t('dashboard.case360.capRedAbove', { pct: '5%' }),
      status: worseAbove(emergencyRate, EMERGENCY_WARN, EMERGENCY_CRIT),
      // Drill-down: matters currently in the emergency provisional-open window.
      onClick: () => navigate('/app/matters?phases=PROVISIONAL_OPEN'),
    },
    {
      key: 'bypass',
      icon: <ShieldAlert size={14} />,
      label: t('dashboard.case360.tileBypassed'),
      value: `${(bypassRate * 100).toFixed(1)}%`,
      caption: t('dashboard.case360.capYellowRed', { warn: '3%', crit: '10%' }),
      status: bypassStatus,
      // Drill-down: matters whose compliance gate was bypassed.
      onClick: () => navigate('/app/matters?compliance=BYPASSED'),
    },
    {
      // D-10 — this tile and the write-off card's ESC figure both used to read
      // "ESC Rate". They are the same formula over different windows, so each
      // now carries its window in the label and its base in the caption.
      // ⚠ Both count *approval* escalations (Task.firstEscalatedAt), NOT the
      // §6.2 estimate_hours overrun — the system has no estimate field.
      key: 'esc',
      icon: <TrendingUp size={14} />,
      label: t('dashboard.case360.tileEscRate', {
        days: escalation?.windowDays ?? 30,
      }),
      value: `${(escRate * 100).toFixed(1)}%`,
      caption: t('dashboard.case360.capEscBase', {
        escalated: escalation?.escalated ?? 0,
        total: escalation?.total ?? 0,
      }),
      status: worseAbove(escRate, ESC_WARN, ESC_CRIT),
    },
    {
      // K-01 — was "Realization", which TS §10.3 defines as
      // Actual_Revenue ÷ Virtual_Cost. This is billable ÷ worked hours.
      key: 'realization',
      icon: <Gauge size={14} />,
      label: t('dashboard.case360.tileBillableShare', {
        days: realization?.windowDays ?? 30,
      }),
      value: realizationValue.toFixed(2),
      caption: t('dashboard.case360.capBillableShareBase', {
        billable: ((realization?.billable ?? 0) / 3600).toFixed(0),
        worked: ((realization?.worked ?? 0) / 3600).toFixed(0),
      }),
      status: worseBelow(realizationValue, REALIZATION_WARN, REALIZATION_CRIT),
    },
  ];

  // Stage 13 — per-matter budget watchlist (breach-only, worst-first), kept
  // below the always-on tiles so the budget consumption signal isn't lost.
  const budgetAlerts: RateAlert[] = budgetMatters.slice(0, BUDGET_ALERT_LIMIT).map((m) => ({
    key: `budget-${m.id}`,
    severity: m.level === 'red' ? 'critical' : 'warning',
    icon: <Wallet size={15} />,
    title: t('dashboard.case360.budgetAlertTitle', { name: m.name ?? `#${m.id}` }),
    description: t('dashboard.case360.budgetAlertDesc', {
      billed: (m.billed ?? 0).toLocaleString(),
      budget: (m.budget ?? 0).toLocaleString(),
    }),
    value: `${((m.rate ?? 0) * 100).toFixed(0)}%`,
    threshold: m.level === 'red' ? '/ 80%' : '/ 75%',
  }));

  return (
    <Card>
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle size={16} className="text-slate-400" />
          {t('dashboard.case360.alertsTitle')}
        </CardTitle>
        <CardDescription>
          {t('dashboard.case360.alertsDesc')}
          {/* Every tile here measures a rolling window, never the page's period
              selector. Saying so on the block is what stops the escalation rate
              here reading as a contradiction of the write-off card's figure for
              the same day (2026-07-26 review, §6.4 — same formula, different
              window). */}
          {' · '}
          {t('dashboard.windowNote', {
            days: escalation?.windowDays ?? realization?.windowDays ?? 30,
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 rounded-lg bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
              {tiles.map((tile) => (
                <MetricTile
                  key={tile.key}
                  icon={tile.icon}
                  label={tile.label}
                  value={tile.value}
                  caption={tile.caption}
                  status={tile.status}
                  onClick={tile.onClick}
                />
              ))}
            </div>
            {budgetAlerts.length > 0 && (
              <div className="mt-4 flex flex-col gap-2.5 border-t border-slate-100 pt-3">
                <p className="text-xs font-medium text-slate-500">
                  {t('dashboard.case360.budgetWatchlist')}
                </p>
                {budgetAlerts.map((a) => (
                  <AlertRow key={a.key} alert={a} />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Pending approvals ────────────────────────────────────────────────────────

// Per-group accent tones (consistent with the stages' colors elsewhere:
// Compliance indigo, Emergency orange, Lead handoffs amber, CRs violet).
interface GroupTone {
  panel: string;
  iconChip: string;
  countChip: string;
}

const GROUP_TONES: Record<
  'compliance' | 'emergency' | 'breakGlass' | 'delegations' | 'billing' | 'changeRequests',
  GroupTone
> = {
  compliance: {
    panel: 'border-indigo-100',
    iconChip: 'bg-indigo-50 text-indigo-600',
    countChip: 'bg-indigo-100 text-indigo-700',
  },
  emergency: {
    panel: 'border-orange-100',
    iconChip: 'bg-orange-50 text-orange-600',
    countChip: 'bg-orange-100 text-orange-700',
  },
  breakGlass: {
    panel: 'border-rose-100',
    iconChip: 'bg-rose-50 text-rose-600',
    countChip: 'bg-rose-100 text-rose-700',
  },
  billing: {
    panel: 'border-emerald-100',
    iconChip: 'bg-emerald-50 text-emerald-600',
    countChip: 'bg-emerald-100 text-emerald-700',
  },
  delegations: {
    panel: 'border-amber-100',
    iconChip: 'bg-amber-50 text-amber-600',
    countChip: 'bg-amber-100 text-amber-700',
  },
  changeRequests: {
    panel: 'border-violet-100',
    iconChip: 'bg-violet-50 text-violet-600',
    countChip: 'bg-violet-100 text-violet-700',
  },
};

// Six pending-approval count tiles (Compliance / Emergency / Break-glass /
// Delegation / Billing / Change Requests). Numbers only — click-through logic
// comes later. Fed by the lightweight `/case360/pending-approvals` endpoint.
function PendingApprovalCountsCard({
  counts,
  isLoading,
}: {
  counts?: Case360PendingCounts;
  isLoading: boolean;
}) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [breakGlassOpen, setBreakGlassOpen] = React.useState(false);
  const [delegationsOpen, setDelegationsOpen] = React.useState(false);
  const [crOpen, setCrOpen] = React.useState(false);

  const tiles = [
    {
      key: 'compliance',
      tone: GROUP_TONES.compliance,
      icon: <ShieldAlert size={16} />,
      label: t('dashboard.case360.groupCompliance'),
      count: counts?.compliance ?? 0,
      onClick: () =>
        navigate('/app/matters?phases=INTAKE,PROVISIONAL_OPEN&compliance=PENDING'),
    },
    {
      key: 'emergency',
      tone: GROUP_TONES.emergency,
      icon: <Zap size={16} />,
      label: t('dashboard.case360.groupEmergency'),
      count: counts?.emergency ?? 0,
      onClick: () => navigate('/app/matters?phases=PROVISIONAL_OPEN'),
    },
    {
      key: 'breakGlass',
      tone: GROUP_TONES.breakGlass,
      icon: <KeyRound size={16} />,
      label: t('dashboard.case360.groupBreakGlass'),
      count: counts?.breakGlass ?? 0,
      onClick: () => setBreakGlassOpen(true),
    },
    {
      key: 'delegation',
      tone: GROUP_TONES.delegations,
      icon: <UserCheck size={16} />,
      label: t('dashboard.case360.groupDelegations'),
      count: counts?.delegation ?? 0,
      onClick: () => setDelegationsOpen(true),
    },
    {
      key: 'billing',
      tone: GROUP_TONES.billing,
      icon: <Receipt size={16} />,
      label: t('dashboard.case360.groupBilling'),
      count: counts?.billing ?? 0,
      // Bills awaiting acceptance are DRAFT — PENDING was retired (2026-07-17)
      // and the governance count is keyed on DRAFT to match.
      onClick: () => navigate('/app/bills?state=DRAFT'),
    },
    {
      key: 'changeRequests',
      tone: GROUP_TONES.changeRequests,
      icon: <GitPullRequest size={16} />,
      label: t('dashboard.case360.groupChangeRequests'),
      count: counts?.changeRequests ?? 0,
      onClick: () => setCrOpen(true),
    },
  ];

  const totalPending = tiles.reduce((sum, tile) => sum + tile.count, 0);

  return (
    <Card>
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardCheck size={16} className="text-slate-400" />
          {t('dashboard.case360.approvalsTitle')}
          {totalPending > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-700">
              {totalPending}
            </span>
          )}
        </CardTitle>
        <CardDescription>{t('dashboard.case360.approvalsDesc')}</CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-[88px] rounded-lg bg-slate-100 animate-pulse" />
              ))
            : tiles.map((tile) => (
                <button
                  key={tile.key}
                  type="button"
                  onClick={tile.onClick}
                  className={`flex w-full flex-col items-start rounded-lg border bg-slate-50/60 p-3 text-left transition-colors hover:border-[#0069D1]/40 hover:bg-white ${tile.tone.panel}`}
                >
                  <span className={`flex h-7 w-7 items-center justify-center rounded-md ${tile.tone.iconChip}`}>
                    {tile.icon}
                  </span>
                  <span className="mt-2 text-2xl font-bold leading-none text-slate-900">
                    {tile.count}
                  </span>
                  <span className="mt-1 truncate text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    {tile.label}
                  </span>
                </button>
              ))}
        </div>
      </CardContent>

      <BreakGlassRequestsDialog open={breakGlassOpen} onOpenChange={setBreakGlassOpen} />
      <DelegationsDialog open={delegationsOpen} onOpenChange={setDelegationsOpen} />
      <ChangeRequestsDialog open={crOpen} onOpenChange={setCrOpen} />
    </Card>
  );
}

// ── Team load ────────────────────────────────────────────────────────────────

const TEAM_LOAD_LIMIT = 8;

function TeamLoadCard({ data, isLoading }: { data?: Case360Governance; isLoading: boolean }) {
  const { t } = useTranslation('common');
  const rows = (data?.teamLoad ?? []).slice(0, TEAM_LOAD_LIMIT);
  const maxTasks = Math.max(1, ...rows.map((r) => r.openTasks));

  return (
    <Card>
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users size={16} className="text-slate-400" />
          {t('dashboard.case360.teamLoadTitle')}
        </CardTitle>
        <CardDescription>{t('dashboard.case360.teamLoadDesc')}</CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="h-24 rounded-md bg-slate-100 animate-pulse" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400">{t('dashboard.case360.groupEmpty')}</p>
        ) : (
          <div className="flex flex-col gap-2">
            {rows.map((u) => (
              <div key={u.id} className="flex items-center gap-3">
                <div className="w-40 shrink-0 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{u.name ?? '—'}</p>
                  <p className="text-xs text-slate-500 truncate">{u.roleName ?? '—'}</p>
                </div>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[#0069D1]"
                    style={{ width: `${Math.round((u.openTasks / maxTasks) * 100)}%` }}
                  />
                </div>
                <div className="w-28 shrink-0 text-right text-xs text-slate-600">
                  {t('dashboard.case360.openTasks', { count: u.openTasks })}
                  {' · '}
                  {t('dashboard.case360.activeMatters', { count: u.activeMatters })}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
